import { useEffect, useState } from "react";

import { DateTime } from "luxon";

import { useQueryClient } from "@tanstack/react-query";

import useSingleLead from "../../../data/useSingleLead";
import useVehiclesMutations from "../../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import Modal from "../../../layouts/Modal";
import { ILead, LeadSource, LeadStatus } from "../../../models/Lead";
import { IVehicle } from "../../../models/Vehicle";
import formatDateTime from "../../../utils/formatDateTime";
import useNotification from "../../notifications/useNotifications";
import Card from "../../shared/Card/Card";
import ConfirmModal from "../../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import { DateTimeInput } from "../../shared/Inputs/DateTimeInput";
import CancelLeadModal from "../CancelLeadModal";
import MarkCollectedModal from "../MarkCollectedModal";
import ProFeatureModal from "../../shared/ProFeature/ProFeatureModal";

export default function LeadStatusCard({ leadId }: { leadId: string }) {
  const { lead, update: updateLead, complete } = useSingleLead(leadId)
  const { update: updateVehicle } = useVehiclesMutations();
  const queryClient = useQueryClient();
  const { addNotification } = useNotification();
  const { saveHandlers } = useDefaultCRUDHandlers("Lead");
  // const [lead, setLead] = useState<ILead>(singleLead.data!.data!);
  const [refresh, setRefresh] = useState(0);
  const [collectionOnOpen, setCollectionOnOpen] = useState(false);
  const [collectedOpen, setCollectedOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [confirmComplete, setConfirmComplete] = useState(false);
  // Kept apart from the open flag so the text doesn't blank while the modal animates closed
  const [proFeatureOpen, setProFeatureOpen] = useState(false);
  const [proFeature, setProFeature] = useState("");
  const [completeLoading, setCompleteLoading] = useState(false);
  const [collectionOn, setCollectionOn] = useState<string | null>(null);
  const [collectionOnSlot, setCollectionOnSlot] = useState<number>(0);
  const [collectedOn, setCollectedOn] = useState<string | null>(null);
  const [leadStatus, setLeadStatus] = useState(LeadStatus["Awaiting Contact"]);

  const handleSaveCollectionOn = () => {
    const updatedVehicle: IVehicle = {
      ...lead.data?.data.vehicle!,
      scheduledCollectionOn: collectionOn,
      scheduledCollectionSlot: collectionOnSlot
    };

    setLeadStatus(LeadStatus["Scheduled"]);
    handleUpdateVehicle(updatedVehicle);
  }

  const handleSaveCollectedOn = () => {
    const collected = lead.data?.data.vehicle?.scheduledCollectionOn ? DateTime.fromISO(lead.data?.data.vehicle?.scheduledCollectionOn) : DateTime.now();

    setCollectedOn(collected.toISODate());
    setLeadStatus(LeadStatus["Collected"]);

    const updatedVehicle: IVehicle = {
      ...lead.data?.data.vehicle!,
      collectedOn: collected.toISODate()
    };

    handleUpdateVehicle(updatedVehicle);
  }

  const saveCollectionSchedule = async (updatedVehicle: IVehicle, status: LeadStatus) => {
    const currentLead = lead.data?.data!;
    const updatedLead: ILead = { ...currentLead, vehicle: updatedVehicle, status: status };

    // Show the change straight away. Saving the lead used to refetch it before the vehicle was
    // saved, which put the old date back on screen until the next refresh.
    await queryClient.cancelQueries(["lead", leadId]);
    const previous = queryClient.getQueryData(["lead", leadId]);
    queryClient.setQueryData(["lead", leadId], (old: any) => old ? { ...old, data: updatedLead } : old);
    setCollectionOnOpen(false);

    try {
      // The date lives on the vehicle, so it is saved before anything refetches the lead
      await updateVehicle.mutateAsync(updatedVehicle);

      if (status !== currentLead.status) {
        await updateLead.mutateAsync(updatedLead);
      }

      saveHandlers.onSuccess();
    } catch {
      queryClient.setQueryData(["lead", leadId], previous);
      saveHandlers.onError();
    } finally {
      queryClient.invalidateQueries(["lead", leadId]);
    }
  }

  const handleClearCollectionOn = () => {
    const updatedVehicle: IVehicle = {
      ...lead.data?.data.vehicle!,
      scheduledCollectionOn: null,
      scheduledCollectionSlot: 0,
      collectionStart: null,
      collectionEnd: null,
    };

    saveCollectionSchedule(updatedVehicle, LeadStatus["Awaiting Contact"]);
  }

  const handleUpdateCollectionOn = () => {
    const updatedVehicle: IVehicle = {
      ...lead.data?.data.vehicle!,
      // Sent without an offset so the API keeps the time as picked - with one, a BST midnight lands on the day before
      scheduledCollectionOn: collectionOn ? DateTime.fromISO(collectionOn).toISO({ includeOffset: false }) : null,
      scheduledCollectionSlot: 0,
      collectionStart: null,
      collectionEnd: null,
    };

    // Correcting the date on a vehicle that has already gone must not drag the lead back to Scheduled
    const pastCollection = [
      LeadStatus["Collected"],
      LeadStatus["Awaiting Payment"],
      LeadStatus["Awaiting COD"],
      LeadStatus["Completed"],
    ].includes(lead.data?.data.status!);

    const status = pastCollection ? lead.data?.data.status! : LeadStatus["Scheduled"];

    saveCollectionSchedule(updatedVehicle, status);
  }

  const handleClearCollectedOn = () => {
    const updatedVehicle: IVehicle = {
      ...lead.data?.data.vehicle!,
      collectedOn: null
    };

    setCollectedOn("");

    if (updatedVehicle.scheduledCollectionOn) {
      setLeadStatus(LeadStatus["Scheduled"]);
    } else {
      setLeadStatus(LeadStatus["Awaiting Contact"]);
    }

    handleUpdateVehicle(updatedVehicle);
  }

  const handleUpdateVehicle = async (vehicle: IVehicle) => {
    await updateVehicle.mutateAsync(vehicle, {
      onSuccess: () => {
        saveHandlers.onSuccess();
        setCollectionOnOpen(false);
        setCollectedOpen(false);

        //Need to wait for the query to update before invalidating lead - somehow the best way to make this work
        setTimeout(() => {
          queryClient.invalidateQueries(["lead", leadId]);
        }, 1000 * 2);
      },
      onError: () => {
        saveHandlers.onError();
      }
    })
  }

  const handleStatusChange = (status: string) => {
    const updatedLead = {
      ...lead.data?.data!,
      status: parseInt(status)
    };

    //Changing to collected without a collected date asks for the date; saving it sets the status
    if (parseInt(status) === LeadStatus["Collected"] && !lead.data?.data.vehicle?.collectedOn) {
      setCollectedOpen(true);
      return;
    }

    //Cancelling always asks for a reason
    if (parseInt(status) === LeadStatus["Cancelled"] && leadStatus !== LeadStatus["Cancelled"]) {
      setCancelOpen(true);
      return;
    }

    setLeadStatus(parseInt(status));
    updateLead.mutate(updatedLead, saveHandlers);
  }

  // These tools exist in Wilma Pro only - Lite has no endpoints behind them yet
  const showProFeature = (feature: string) => {
    setProFeature(feature);
    setProFeatureOpen(true);
  }

  const handleComplete = () => {
    setCompleteLoading(true);

    complete.mutate(lead.data?.data.id!, {
      onSuccess: (res: any) => {
        setConfirmComplete(false);
        // Says whether the customer was emailed, which the generic saved message would hide
        addNotification({
          variant: "success",
          primaryText: res.message,
        });
      },
      onError: (res: any) => {
        addNotification({
          variant: "error",
          primaryText: res.message,
        })
      },
      onSettled: () => {
        setCompleteLoading(false);
      }
    })
  }

  useEffect(() => {
    if (lead.isLoading || !lead.data?.data) return;

    // console.log(lead.data.data)
    setLeadStatus(lead.data.data.status);
    setCollectionOn(lead.data.data.vehicle?.scheduledCollectionOn ?? "");
    setCollectionOnSlot(lead.data.data.vehicle?.scheduledCollectionSlot ?? 0);
    setCollectedOn(lead.data.data.vehicle?.collectedOn ?? "");
    setLeadStatus(lead.data.data.status);

  }, [lead.isLoading, lead.data?.data])

  return (
    <>
      <ConfirmModal open={confirmComplete} setOpen={setConfirmComplete} onConfirm={() => handleComplete()}
        title="Complete Lead" message={`Really complete this lead?\nThis will update the status to 'Complete' and send the customer a confirmation email.`}
        confirmButtonText="Complete" isLoading={completeLoading}
      />
      <Modal open={collectionOnOpen} setOpen={setCollectionOnOpen} width="sm:max-w-4xl">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-x-4">
          <label htmlFor="collectionOn" className="shrink-0">Set Collection Scheduled On:</label>

          <input
            type="datetime-local"
            className={"w-full text-sm text-gray-900 border-1 border-gray-300 rounded-md shadow-sm"}
            value={collectionOn ?? ""}
            onChange={(e) => setCollectionOn(e.target.value)}
            name="collectionOn"
          />

          <button
            onClick={() => handleClearCollectionOn()}
            className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
          >
            Clear
          </button>

          <button
            onClick={() => handleUpdateCollectionOn()}
            className="inline-flex w-full justify-center rounded-md bg-primary-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
          >
            Update
          </button>
        </div>

      </Modal>
      <MarkCollectedModal
        open={collectedOpen}
        setOpen={setCollectedOpen}
        lead={lead.data?.data ?? null}
        onCollected={(date) => {
          setCollectedOn(date);
          setLeadStatus(LeadStatus["Collected"]);
        }}
      />
      <CancelLeadModal
        open={cancelOpen}
        setOpen={setCancelOpen}
        lead={lead.data?.data ?? null}
        onCancelled={() => setLeadStatus(LeadStatus["Cancelled"])}
      />
      <ProFeatureModal open={proFeatureOpen} setOpen={setProFeatureOpen} feature={proFeature} />
      <Card
        bodyClassName="p-4"
        title="Lead Status"
      >
        <div className="md:grid md:grid-cols-5 gap-3">

          <div className="col-span-5 divide-y ">

            <div className="py-2 grid grid-cols-2 gap-4">
              <dt className="text-sm font-medium text-gray-500">
                Lead Source
              </dt>
              <dd className="items-center text-sm text-gray-900">
                <div>
                  {LeadSource[lead.data?.data.website!]}
                </div>
              </dd>
            </div>
            {lead.data?.data.preferredCollectionDate && (
              <div className="py-2 grid grid-cols-2 gap-4">
                <dt className="text-sm font-medium text-gray-500">
                  Preferred Collection Date
                </dt>
                <dd className="items-center text-sm text-gray-900">
                  {formatDateTime(lead.data.data.preferredCollectionDate, DateTime.DATE_SHORT)}
                </dd>
              </div>
            )}
            <div className="py-2 grid grid-cols-2 gap-4">
              <dt className="text-sm font-medium text-gray-500">
                Collection Scheduled On
              </dt>
              <dd className="flex justify-between items-center text-sm text-gray-900">
                <div>
                  {collectionOn ? formatDateTime(collectionOn, DateTime.DATE_SHORT) : "Not Set"}
                </div>
                <button
                  type="button"
                  onClick={() => setCollectionOnOpen(true)}
                  className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                  Change
                </button>
              </dd>
            </div>
            <div className="py-2 grid grid-cols-2 gap-4">
              <dt className="text-sm font-medium text-gray-500">
                Collected On
              </dt>
              <dd className="flex justify-between items-center text-sm text-gray-900">
                <div>
                  {collectedOn ? formatDateTime(collectedOn, DateTime.DATE_SHORT) : "Not Set"}
                </div>
                {collectedOn ? (
                  <button
                    type="button"
                    onClick={() => handleClearCollectedOn()}
                    className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                    Clear
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setCollectedOpen(true)}
                    className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                    Set as collected
                  </button>
                )}

              </dd>
            </div>
            <div className="py-2 grid grid-cols-2 gap-4">
              <dt className="text-sm font-medium text-gray-500">
                COD Required
              </dt>
              <dd className="flex justify-between items-center text-sm text-gray-900">
                <div>
                  {lead.data?.data.vehicle?.codRequired ? "Yes" : "No"}
                </div>
                {!lead.data?.data.vehicle?.codCertificateNumber && (
                  <button
                    type="button"
                    onClick={() => showProFeature("Tracking COD requirements")}
                    className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                    Toggle
                  </button>
                )}
              </dd>
            </div>

            {(lead.data?.data.vehicle?.codRequired || lead.data?.data.vehicle?.codCertificateNumber) && (
              <>
                <div className="py-2 grid grid-cols-2 gap-4">
                  <dt className="text-sm font-medium text-gray-500">
                    COD Confirmed
                  </dt>
                  <dd className="flex justify-between items-center text-sm text-gray-900">
                    <div>
                      {lead.data?.data.vehicle?.codConfirmedOn ? "Yes" : "No"}
                    </div>

                    {!lead.data?.data.vehicle?.codCertificateNumber && (
                      <button
                        type="button"
                        onClick={() => showProFeature("Confirming CODs")}
                        className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                        Toggle
                      </button>
                    )}
                  </dd>
                </div>

                <div className="py-2 grid grid-cols-2 gap-4">
                  <dt className="text-sm font-medium text-gray-500">
                    {lead.data?.data.vehicle?.codCertificateNumber ? "COD Certificate Number" : "Generate COD"}
                  </dt>
                  <dd className="flex justify-between items-center text-sm text-gray-900">
                    <span>{lead.data?.data.vehicle?.codCertificateNumber ?? ""}</span>
                    {!lead.data?.data.vehicle?.codCertificateNumber ? (
                      <button
                        type="button"
                        onClick={() => showProFeature("Generating COD certificates")}
                        className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                        Generate
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => showProFeature("Downloading COD certificates")}
                        className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                        Download Certificate
                      </button>
                    )}
                  </dd>
                </div>

              </>
            )}

            <div className="py-2 grid grid-cols-2 gap-4">
              <dt className="flex items-center text-sm font-medium text-gray-500">
                Status
              </dt>
              <dd className="text-sm text-gray-900">
                <select
                  // defaultValue={lead.status}
                  value={leadStatus}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="border border-gray-400 rounded-md text-sm"
                >
                  {Object.values(LeadStatus).filter((ct) => isNaN(Number(ct))).map((status, i) => (
                    <option key={status} value={i}>
                      {status}
                    </option>
                  ))}
                </select>
              </dd>
            </div>

            {leadStatus === LeadStatus["Cancelled"] && lead.data?.data.cancellationReason && (
              <div className="py-2 grid grid-cols-2 gap-4">
                <dt className="text-sm font-medium text-gray-500">
                  Cancelled
                </dt>
                <dd className="text-sm text-gray-900">
                  <div className="font-medium text-red-700">
                    {lead.data.data.cancellationReason}
                    {lead.data.data.cancelledOn && ` · ${formatDateTime(lead.data.data.cancelledOn, DateTime.DATE_SHORT)}`}
                  </div>
                  {lead.data.data.cancellationNote && (
                    <div className="mt-0.5 whitespace-pre-line text-gray-600">{lead.data.data.cancellationNote}</div>
                  )}
                </dd>
              </div>
            )}

            <div className="pt-3 flex justify-between items-center">
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-3 gap-y-3">
                <div className="flex gap-x-3">
                  <ButtonInput label="Send Collection Note" classes="" isSubmit={false} onClick={() => showProFeature("Emailing collection notes")} />

                  <ButtonInput label="Print Collection Note" classes="" isSubmit={false} onClick={() => showProFeature("Printing collection notes")} />
                </div>

                {!lead.data?.data.vehicle?.codCertificateNumber && (
                  <div className="flex gap-x-3">
                    <ButtonInput label="Send COD Certificate" classes="" isSubmit={false} onClick={() => showProFeature("Emailing COD certificates")} />

                    <ButtonInput label="Send Both" classes="" isSubmit={false} onClick={() => showProFeature("Emailing lead documents")} />
                  </div>
                )}
              </div>
              <div className="flex gap-x-3">
                {leadStatus !== LeadStatus["Cancelled"] && leadStatus !== LeadStatus["Completed"] && !collectedOn && (
                  <button
                    type="button"
                    onClick={() => setCancelOpen(true)}
                    className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium leading-4 text-red-700 shadow-sm hover:bg-red-50 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    Cancel Lead
                  </button>
                )}
                <ButtonInput label="Complete Lead" classes={lead.data?.data.completedOn && "bg-green-600 hover:bg-green-700"}
                  isSubmit={false} onClick={() => setConfirmComplete(true)}
                />
              </div>
            </div>
          </div>

        </div>
      </Card >
    </>
  );
}
